import { useState } from 'react';
import type { Flow, Automacao } from '../../types';
import { PlusIcon, TrashIcon, PencilIcon, ChevronDown, ChevronRight, CopyIcon } from 'lucide-react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import NovaAutomacao from './NovaAutomacao';
import Toggle from '../common/Toggle';

interface AutomacoesProps {
  flows: Flow[];
  automacoes: Automacao[];
  onSave: (automacoes: Automacao[]) => void;
  session?: { usuario: { role?: string; empresaId?: string; } };
  canView?: boolean;       // Se definido, sobrescreve a checagem de admin para visualizar
  canEdit?: boolean;       // Se definido, sobrescreve a checagem de admin para editar
  allowedFlowIds?: string[]; // Se definido, filtra apenas estes fluxos
}

const Automacoes = ({ flows, automacoes, onSave, session, canView, canEdit, allowedFlowIds }: AutomacoesProps) => {
  const [showNovaAutomacao, setShowNovaAutomacao] = useState(false);
  const [editingAutomacao, setEditingAutomacao] = useState<Automacao | null>(null);
  const [expandedFlows, setExpandedFlows] = useState<Set<string>>(new Set()); // Minimizado por padrão

  // Habilitar/Desabilitar (pause/continue) helpers
  const toggleAutomacao = (id: string) => {
    const updated = automacoes.map(a => {
      const nextStatus: 'ativo' | 'inativo' = ((a.status ?? 'ativo') === 'ativo') ? 'inativo' : 'ativo';
      return a.id === id ? { ...a, status: nextStatus } : a;
    });
    onSave(updated);
  };

  const toggleAllAutomacoes = () => {
    const anyActive = automacoes.some(a => (a.status ?? 'ativo') === 'ativo');
    const nextStatus: 'ativo' | 'inativo' = anyActive ? 'inativo' : 'ativo';
    onSave(automacoes.map(a => ({ ...a, status: nextStatus })));
  };

  const handleDeleteAutomacao = (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta automação?')) return;
    onSave(automacoes.filter(a => a.id !== id));
  };

  const handleCloneAutomacao = (automacao: Automacao) => {
    // Deep clone para não compartilhar referências de ações/regras
    const clone: Automacao = JSON.parse(JSON.stringify(automacao));
    clone.id = String(Date.now());
    clone.nome = `${automacao.nome || 'Automação'} (cópia)`;
    clone.status = 'inativo'; // começa pausada pra evitar disparo duplicado
    onSave([...automacoes, clone]);
  };

  const handleEditAutomacao = (automacao: Automacao) => {
    setEditingAutomacao(automacao);
  };

  const handleSaveEdit = (automacao: Omit<Automacao, 'id'>) => {
    console.log('💾 handleSaveEdit chamado com:', automacao);
    console.log('🔍 ID da automação sendo editada:', editingAutomacao?.id);
    
    const updatedAutomacao = {
      ...automacao,
      id: editingAutomacao!.id
    };
    
    console.log('🎯 Automação final para salvar:', updatedAutomacao);
    console.log('📋 Ações da automação atualizada:', updatedAutomacao.acoes);
    
    onSave(automacoes.map(a => 
      a.id === updatedAutomacao.id ? updatedAutomacao : a
    ));
    setEditingAutomacao(null);
  };

  const toggleFlow = (flowId: string) => {
    setExpandedFlows(prev => {
      const next = new Set(prev);
      if (next.has(flowId)) {
        next.delete(flowId);
      } else {
        next.add(flowId);
      }
      return next;
    });
  };

  // Verificar se usuário é administrador
  const isAdmin = ['administrator', 'admin'].includes(session?.usuario?.role as any);
  // canView/canEdit permitem acesso granular a não-admins
  const effectiveCanView = canView !== undefined ? canView : isAdmin;
  const effectiveCanEdit = canEdit !== undefined ? canEdit : isAdmin;

  // Agrupar automações por fluxo (filtrando por allowedFlowIds se definido)
  const flowsVisiveis = allowedFlowIds && allowedFlowIds.length > 0
    ? flows.filter(f => allowedFlowIds.includes(f.id))
    : flows;
  const automacoesGrouped = flowsVisiveis.map(flow => ({
    flow,
    automacoes: automacoes.filter(a => a.flowId === flow.id)
  })).filter(group => group.automacoes.length > 0);

  if (!effectiveCanView) {
    return (
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center py-16">
          <ExclamationTriangleIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Acesso Restrito</h2>
          <p className="text-gray-500">
            Apenas administradores podem gerenciar automações.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-2">
      <div className="max-w-7xl mx-auto">
        <div className="space-y-3">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <div>
              <h1 className="text-sm font-semibold text-gray-900">
                Automações
              </h1>
              <p className="text-xs text-gray-500">
                Configure automações para seus fluxos de trabalho
              </p>
            </div>
            {effectiveCanEdit && (
              <div className="flex gap-2 items-center">
                <Toggle
                  checked={automacoes.some(a => (a.status ?? 'ativo') === 'ativo')}
                  onChange={() => toggleAllAutomacoes()}
                  size="md"
                  label={automacoes.some(a => (a.status ?? 'ativo') === 'ativo') ? 'Pausar todas' : 'Ativar todas'}
                />
                <button
                  onClick={() => setShowNovaAutomacao(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors text-xs font-medium"
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                  <span>Nova Automação</span>
                </button>
              </div>
            )}
          </div>

          {/* Lista de Automações */}
          {automacoes.length === 0 ? (
            <div className="text-center py-12 bg-white/80 backdrop-blur-md rounded-lg border border-gray-200/50">
              <div className="space-y-3">
                <svg className="h-8 w-8 text-gray-300 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <p className="text-xs text-gray-500">Nenhuma automação configurada. Comece criando sua primeira.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {automacoesGrouped.map(({ flow, automacoes: flowAutomacoes }) => {
                const isExpanded = expandedFlows.has(flow.id);
                const activeCount = flowAutomacoes.filter(a => (a.status ?? 'ativo') === 'ativo').length;
                
                return (
                  <div key={flow.id} className="bg-white/80 backdrop-blur-md rounded-lg border border-gray-200/50 overflow-hidden">
                    {/* Header do Fluxo */}
                    <button
                      onClick={() => toggleFlow(flow.id)}
                      className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-50/50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-gray-600" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-600" />
                        )}
                        <div className="text-left">
                          <h3 className="text-[0.8rem] font-semibold text-gray-900">
                            {flow.name}
                          </h3>
                          <p className="text-[0.65rem] text-gray-600 mt-0">
                            {flowAutomacoes.length} automação{flowAutomacoes.length !== 1 ? 'ões' : ''} • {activeCount} ativa{activeCount !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`px-2 py-0.5 rounded-full text-[0.65rem] font-medium ${
                          activeCount > 0 ? 'bg-indigo-50 text-indigo-600' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {activeCount}/{flowAutomacoes.length}
                        </div>
                      </div>
                    </button>

                    {/* Grid de Automações - Cards Compactos */}
                    {isExpanded && (
                      <div className="px-3 pb-3">
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-2">
                          {flowAutomacoes.map((automacao) => {
                            const etapaOrigem = flow.etapas?.find(e => e.id === automacao.etapaOrigemId);
                            const isAtiva = (automacao.status ?? 'ativo') === 'ativo';
                            
                            // Ações simplificadas
                            const temMover = automacao.acoes?.some(a => a.tipo === 'mover_card');
                            const temEmail = automacao.acoes?.some(a => a.tipo === 'enviar_email');
                            const temWhatsapp = automacao.acoes?.some(a => a.tipo === 'enviar_whatsapp');
                            const temVisibilidade = automacao.acoes?.some(a => a.tipo === 'exibir_ocultar');
                            const temCardConectado = automacao.acoes?.some(a => a.tipo === 'criar_card_conectado');
                            const totalCondicoes = automacao.acoes?.reduce((acc, a) => 
                              acc + (a.tipo === 'exibir_ocultar' ? (a.configuracao?.regras?.length || 0) : 0), 0) || 0;

                            return (
                              <div 
                                key={automacao.id} 
                                className={`bg-white rounded-lg border border-gray-200/50 hover:border-gray-300 hover:shadow-sm transition-all ${effectiveCanEdit ? 'cursor-pointer' : 'cursor-default'} group`}
                                onClick={() => effectiveCanEdit && handleEditAutomacao(automacao)}
                              >
                                {/* Header */}
                                <div className={`px-2 py-1 border-b ${isAtiva ? 'bg-gray-50 border-gray-100' : 'bg-gray-50/50 border-gray-100'}`}>
                                  <div className="flex items-center justify-between gap-1">
                                    <div className="flex items-center gap-1 min-w-0 flex-1">
                                      <span className="text-xs">⚡</span>
                                      <span className="font-medium text-[0.65rem] text-gray-800 truncate" title={automacao.nome || 'Automação'}>
                                        {automacao.nome || 'Automação'}
                                      </span>
                                    </div>
                                    {effectiveCanEdit && (
                                      <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleEditAutomacao(automacao); }}
                                          className="p-0.5 text-gray-400 hover:text-blue-600 rounded"
                                          title="Editar"
                                        >
                                          <PencilIcon className="h-2.5 w-2.5" />
                                        </button>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleCloneAutomacao(automacao); }}
                                          className="p-0.5 text-gray-400 hover:text-emerald-600 rounded"
                                          title="Clonar"
                                        >
                                          <CopyIcon className="h-2.5 w-2.5" />
                                        </button>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleDeleteAutomacao(automacao.id); }}
                                          className="p-0.5 text-gray-400 hover:text-red-600 rounded"
                                          title="Excluir"
                                        >
                                          <TrashIcon className="h-2.5 w-2.5" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-0.5">
                                    <div className={`w-1 h-1 rounded-full ${isAtiva ? 'bg-emerald-500' : 'bg-gray-400'}`}></div>
                                    <span className={`text-[0.55rem] ${isAtiva ? 'text-emerald-600' : 'text-gray-500'}`}>
                                      {isAtiva ? 'Ativa' : 'Pausada'}
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Corpo */}
                                <div className="px-2 py-1.5 space-y-1">
                                  {/* Etapa */}
                                  <div className="flex items-center gap-1 text-[0.6rem]">
                                    <span className="text-blue-500 text-[0.5rem]">●</span>
                                    <span className="text-gray-500">Etapa:</span>
                                    <span className="text-gray-700 font-medium truncate">{etapaOrigem?.nome || 'Todas'}</span>
                                  </div>
                                  
                                  {/* Tags de ações */}
                                  <div className="flex flex-wrap gap-0.5">
                                    {temMover && (
                                      <span className="inline-flex items-center px-1 py-0.5 bg-blue-50 text-blue-600 rounded text-[0.5rem]">
                                        🔄 Mover
                                      </span>
                                    )}
                                    {temEmail && (
                                      <span className="inline-flex items-center px-1 py-0.5 bg-purple-50 text-purple-600 rounded text-[0.5rem]">
                                        📧
                                      </span>
                                    )}
                                    {temWhatsapp && (
                                      <span className="inline-flex items-center px-1 py-0.5 bg-green-50 text-green-600 rounded text-[0.5rem]">
                                        📱
                                      </span>
                                    )}
                                    {temVisibilidade && (
                                      <span className="inline-flex items-center px-1 py-0.5 bg-orange-50 text-orange-600 rounded text-[0.5rem]">
                                        👁️ {totalCondicoes > 0 ? totalCondicoes : ''}
                                      </span>
                                    )}
                                    {temCardConectado && (
                                      <span className="inline-flex items-center px-1 py-0.5 bg-violet-50 text-violet-600 rounded text-[0.5rem]">
                                        🔗
                                      </span>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Toggle */}
                                {effectiveCanEdit && (
                                  <div className="px-2 py-1 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
                                    <Toggle
                                      checked={isAtiva}
                                      onChange={() => toggleAutomacao(automacao.id)}
                                      size="sm"
                                      label={isAtiva ? 'Pausar' : 'Ativar'}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Modal de Nova/Edição de Automação */}
          {(showNovaAutomacao || editingAutomacao) && (
            <NovaAutomacao
              flows={flows.map(flow => ({ ...flow, nome: flow.name }))}
              automacaoExistente={editingAutomacao as any}
              session={session as any}
              onSave={(automacao: any) => {
                if (editingAutomacao) {
                  handleSaveEdit(automacao);
                } else {
                  const newAutomacao: Automacao = {
                    ...automacao,
                    id: String(Date.now())
                  };
                  onSave([...automacoes, newAutomacao]);
                }
                setShowNovaAutomacao(false);
              }}
              onClose={() => {
                setShowNovaAutomacao(false);
                setEditingAutomacao(null);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Automacoes;